import { type EditorContextValue } from '@tiptap/react';
import { useEffect, useState } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Popover } from '@/components/ui/popover';
import { withUIDelay } from '@/lib/animate';
import type { KeyName } from '@/lib/key';
import { classList } from '@/utils/cn';

import { useEditorFloatingContainer } from '../base-editor-context';
import { type EditorFeatureSet } from '../BaseEditor';
import { EditorBubbleItem, useEditorState } from '../components';
import { editorEventBus } from '../utils/event';

export type SelectorItem = {
  name: string;
  icon: LBIcon;
  tooltip?: string;
  hotkey?: KeyName[];
  command: (editor: NonNullable<EditorContextValue['editor']>) => void;
  isActive: (editor: NonNullable<EditorContextValue['editor']>) => boolean;
};

const items: SelectorItem[] = [
  {
    name: 'Text',
    icon: Icons.text,
    command: (editor) =>
      editor.chain().focus().toggleNode('paragraph', 'paragraph').run(),
    isActive: (editor) =>
      editor.isActive('paragraph') &&
      !editor.isActive('bulletList') &&
      !editor.isActive('orderedList'),
  },
  {
    name: 'Heading 1',
    icon: Icons.heading1,
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
  },
  {
    name: 'Heading 2',
    icon: Icons.heading2,
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
  },
  {
    name: 'Heading 3',
    icon: Icons.heading3,
    command: (editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
  },
  {
    name: 'To-do List',
    icon: Icons.todoList,
    command: (editor) => editor.chain().focus().toggleTaskList().run(),
    isActive: (editor) => editor.isActive('taskItem'),
  },
  {
    name: 'Bullet List',
    icon: Icons.bulletList,
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
    isActive: (editor) => editor.isActive('bulletList'),
  },
  {
    name: 'Numbered List',
    icon: Icons.numList,
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
    isActive: (editor) => editor.isActive('orderedList'),
  },
  {
    name: 'Quote',
    icon: Icons.quote,
    command: (editor) =>
      editor
        ?.chain()
        .focus()
        .toggleNode('paragraph', 'paragraph')
        .toggleBlockquote()
        .run(),
    isActive: (editor) => editor.isActive('blockquote'),
  },
  {
    name: 'Code',
    icon: Icons.code,
    command: (editor) => editor.chain().focus().toggleCodeBlock().run(),
    isActive: (editor) => editor.isActive('codeBlock'),
  },
];

// Node/block types hidden in the `email` feature set. Keyed by their menu label,
// shared by the bubble `NodeSelector` and the slash menu.
export const EMAIL_HIDDEN_NODES = new Set(['To-do List']);
const COMPOSER_NODE_ORDER = [
  'Text',
  'Heading 1',
  'Heading 2',
  'Heading 3',
  'Numbered List',
  'Bullet List',
  'Code',
];

const getComposerLabel = (name: string) => {
  if (name === 'Numbered List') return 'Numbered list';
  if (name === 'Bullet List') return 'Bulleted list';
  if (name === 'Code') return 'Code block';
  return name;
};

const COMPOSER_LABEL_CLASSES: Record<string, string> = {
  'Heading 1': 'text-lg font-semibold',
  'Heading 2': 'text-base font-semibold',
  'Heading 3': 'text-sm font-semibold',
};

interface NodeSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureSet?: EditorFeatureSet | 'composer';
}

export const NodeSelector = ({
  open,
  onOpenChange: setOpen,
  featureSet = 'full',
}: NodeSelectorProps) => {
  const editor = useEditorState();
  const container = useEditorFloatingContainer();
  const [isClosing, setIsClosing] = useState(false);
  const [cachedActiveItem, setCachedActiveItem] = useState<{ name: string }>();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentActiveItem = items
    .filter((item) => editor && item.isActive(editor))
    .pop() ?? {
    name: 'Multiple',
  };

  useEffect(() => {
    if (!editor) return;

    const handleWillClose = () => {
      setCachedActiveItem(currentActiveItem);
      setIsClosing(true);
      withUIDelay(() => setIsClosing(false));
    };

    const editorBubbleWillCloseEventSub = editorEventBus.on(
      'EDITOR_BUBBLE_WILL_CLOSE',
      handleWillClose
    );

    return () => editorBubbleWillCloseEventSub?.unsubscribe();
    // The listener needs the current active item; retain the existing subscription lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, currentActiveItem]);

  if (!editor) return null;

  const activeItem =
    isClosing && cachedActiveItem ? cachedActiveItem : currentActiveItem;

  const visibleItems = items
    .filter(
      (item) => featureSet !== 'email' || !EMAIL_HIDDEN_NODES.has(item.name)
    )
    .filter(
      (item) =>
        featureSet !== 'composer' || COMPOSER_NODE_ORDER.includes(item.name)
    )
    .sort((a, b) =>
      featureSet === 'composer'
        ? COMPOSER_NODE_ORDER.indexOf(a.name) -
          COMPOSER_NODE_ORDER.indexOf(b.name)
        : 0
    );
  const activeLabel =
    featureSet === 'composer'
      ? getComposerLabel(activeItem.name)
      : activeItem.name;

  return (
    <Popover.Root modal={true} open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild className="gap-2 focus:ring-0">
        <Button
          size="xs"
          variant="ghost"
          className={classList(
            'text-foreground-subtle gap-1 [&[data-state=open]>svg]:rotate-180',
            featureSet === 'composer' && 'h-7 bg-surface-active px-2'
          )}
          tooltip="Turn into"
          tooltipOptions={{ sideOffset: 9 }}
          onPointerDown={(e) => {
            if (open) e.stopPropagation();
          }}
        >
          <span className="whitespace-nowrap text-sm">{activeLabel}</span>
          <Icons.chevronDown className="size-4 text-foreground-muted transition-transform" />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        container={container}
        portalled
        sideOffset={10}
        align="start"
        className="w-48 p-1 border border-border-strong motion-scale-in-95 motion-opacity-in-0 motion-duration-200"
        style={{
          transformOrigin: 'var(--radix-popper-transform-origin)',
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {featureSet !== 'composer' && (
          <div className="my-1 px-2 text-xs text-foreground-muted">
            Turn into
          </div>
        )}
        {visibleItems.map((item, index) => (
          <EditorBubbleItem
            key={index}
            asChild
            onSelect={(editor) => {
              item.command(editor);
              setOpen(false);
            }}
            className={classList(
              'flex w-full cursor-pointer items-center justify-between rounded-md smoothing-default px-2 text-left text-sm hover:bg-surface-active',
              featureSet === 'composer' ? 'py-2' : 'py-1',
              featureSet === 'composer' &&
                activeItem.name === item.name &&
                'bg-surface-active'
            )}
          >
            <button type="button">
              <div className="flex items-center space-x-2">
                {featureSet !== 'composer' && (
                  <div className="rounded-md smoothing-default p-1">
                    <item.icon className="size-3" />
                  </div>
                )}
                <span
                  className={
                    featureSet === 'composer'
                      ? COMPOSER_LABEL_CLASSES[item.name]
                      : undefined
                  }
                >
                  {featureSet === 'composer'
                    ? getComposerLabel(item.name)
                    : item.name}
                </span>
              </div>
              {featureSet !== 'composer' && activeItem.name === item.name && (
                <Icons.check className="h-4 w-4" />
              )}
            </button>
          </EditorBubbleItem>
        ))}
      </Popover.Content>
    </Popover.Root>
  );
};
